import { type JSX, useCallback, useRef } from 'react';
import { cx } from '@linaria/core';
import { formatInTimeZone } from 'date-fns-tz';

import { categoryGroups, REPUBS } from '@cx-modules/constants';
import { useTranslation } from '@cx-modules/i18n-provider';
import { type SubeventType } from '@cx-modules/types';
import { Accordion } from '@cx-modules/ui';
import { extractTeamsFromSubeventName } from '@cx-modules/utils';

import { closeGridToolTipLocalStorage } from '../../helpers';
import { getNumberOfColumns } from '../../helpers/get-number-of-columns';
import { useBetsWithBestOdds } from '../../hooks/useBetsWithBestOdds';
import { useGroupedBets } from '../../hooks/useGroupedBets';
import { BUTTON_TEXT, useShowMore } from '../../hooks/useShowMore';
import { DesktopOnly } from '../../OddsGrid.style';
import { useSelector } from '../../providers/StoreProvider';
import { getMarketLabel } from '../../utils/labels';
import { Chevron } from '../Chevron';
import { SeeMoreOddsTip } from '../SeeMoreOddsTip/SeeMoreOddsTip';

import { BET_INCREMENT_NUMBER, MAX_NUMBER_OF_COLUMNS } from './config';
import { MarketExpanderGrouped } from './MarketExpanderGrouped';
import { MarketExpanderSimple } from './MarketExpanderSimple';
import { PlayerBettingMarketExpander } from './PlayerBettingMarketExpander';
import {
  AccordionHeader,
  AccordionWrapper,
  ExpanderContent,
  ExpanderOuterWrapper,
  GridToggler,
  MarketHeaderText,
  Pointer,
  ShowMoreContainer,
  ShowMoreText,
  UnsupportedMessageContent,
  UnsupportedMessageWrapper,
} from './style';

export const MarketExpander = ({
  gridIsExpanded,
  marketId,
  marketName,
  showCompareOddsToolTip,
  showMoreBetsStartsExpanded,
  showSubeventData,
  startsExpanded,
  subeventType,
  toggleGrid,
  toggleMarket,
}: {
  gridIsExpanded: boolean;
  marketId: number;
  marketName: string;
  showCompareOddsToolTip: boolean;
  showMoreBetsStartsExpanded?: boolean;
  showSubeventData?: boolean;
  startsExpanded: boolean;
  subeventType: SubeventType;
  toggleGrid: () => void;
  toggleMarket: () => void;
}): JSX.Element => {
  const categoryGroupId = useSelector(store => store.categoryGroupId);
  const categoryGroup = categoryGroups.find(cg => cg.id === categoryGroupId);
  const categoryNs = categoryGroup?.translationFile || '';
  const ns = [categoryNs, 'label', 'bettype'];
  const { t } = useTranslation(ns);

  const betsWithBestOdds = useBetsWithBestOdds().filter(bet => bet.odds.length > 0);

  const allowOddsClickout = useSelector(store => store.allowOddsClickout);
  const repub = useSelector(state => state.repub);

  const subeventName = useSelector(store => store.subeventName);
  const subeventStartTime = useSelector(store => store.subeventStartTime);

  const seeMoreOdds = useSelector(store => store.seeMoreOddsTooltip);
  const setSeeMoreOddsTooltip = useSelector(state => state.setSeeMoreOddsTooltip);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const playerMarketIncludedHeaders = ['jugador', 'player', 'giocator'];
  const playerMarketRegex = new RegExp(playerMarketIncludedHeaders.join('|'), 'i');

  // These player betting markets are displayed in a specific way.
  // The string check for these is really basic and dirty, we will need to check if it serves purpose with all markets.
  const isPlayerBettingMarket = playerMarketRegex.test(marketName);

  const handleCompareAllOddsClick = useCallback(() => {
    setSeeMoreOddsTooltip(false);
    toggleGrid();
    closeGridToolTipLocalStorage();
  }, [setSeeMoreOddsTooltip, toggleGrid]);

  const gridTipVisible = seeMoreOdds && showCompareOddsToolTip;

  const numberOfColumns = Math.min(betsWithBestOdds.length, MAX_NUMBER_OF_COLUMNS);

  const numberOfInitialRowsForOutrights = 2;

  function getInitialVisibleBetsNumber(numberOfColumns: number, subeventType: SubeventType) {
    switch (subeventType) {
      case 'MATCH':
        return numberOfColumns;
      case 'OUTRIGHT':
        return numberOfColumns * numberOfInitialRowsForOutrights;
      case 'RACE':
        return numberOfColumns;
      case 'ANTEPOST':
        return numberOfColumns;
      default:
        return numberOfColumns;
    }
  }

  const numberOfBetsForShowMoreBetsStartsExpanded = BET_INCREMENT_NUMBER * 2;

  const { setVisibleBetsNumber, showMoreButtonText, visibleBetsNumber } = useShowMore({
    allBetsNumber: betsWithBestOdds.length,
    betIncrementNumber:
      BET_INCREMENT_NUMBER *
      getNumberOfColumns({
        betsWithBestOdds,
        isPlayerBettingMarket,
        numberOfColumns,
      }),
    containerRef: wrapperRef,
    initialVisibleBetsNumber: showMoreBetsStartsExpanded
      ? numberOfBetsForShowMoreBetsStartsExpanded
      : getInitialVisibleBetsNumber(numberOfColumns, subeventType),
    scrollTopNumber: 165,
  });

  const { canBeGrouped, visibleGroupedBets } = useGroupedBets({
    betsWithBestOdds,
    visibleBetsNumber,
  });

  const showMoreBetsButtonVisible = () => {
    if (showMoreBetsStartsExpanded) {
      return betsWithBestOdds.length > numberOfBetsForShowMoreBetsStartsExpanded;
    }

    return betsWithBestOdds.length > numberOfColumns;
  };

  const marketNameLabel = getMarketLabel({ marketName, t });
  const compareAllOddsLabel = t('CX_ODDS_GRID_COMPARE_ALL_ODDS', {
    ns: 'label',
  });

  const renderSupportMessage = () => {
    if (allowOddsClickout) return null;

    return (
      <UnsupportedMessageWrapper>
        <UnsupportedMessageContent>{t('CX_ODDS_GRID_UNSUPPORTED_STATE', { ns: 'label' })}</UnsupportedMessageContent>
      </UnsupportedMessageWrapper>
    );
  };

  const renderMarketExpander = () => {
    let ExpanderContent;

    if (isPlayerBettingMarket) {
      ExpanderContent = (
        <PlayerBettingMarketExpander bets={betsWithBestOdds.slice(0, visibleBetsNumber)} marketId={marketId} />
      );
    } else if (canBeGrouped) {
      ExpanderContent = <MarketExpanderGrouped groupedBets={visibleGroupedBets} />;
    } else {
      ExpanderContent = <MarketExpanderSimple bets={betsWithBestOdds.slice(0, visibleBetsNumber)} />;
    }

    return <ExpanderOuterWrapper>{ExpanderContent}</ExpanderOuterWrapper>;
  };

  const renderShowMoreContainer = () => {
    if (!showMoreBetsButtonVisible()) return null;

    return (
      <ShowMoreContainer aria-expanded={showMoreButtonText === BUTTON_TEXT.SHOW_LESS}>
        <ShowMoreText data-testid="show-more-less" onClick={setVisibleBetsNumber}>
          {t(showMoreButtonText, { ns: 'label' })}
        </ShowMoreText>
        <Chevron expanded={showMoreButtonText === BUTTON_TEXT.SHOW_LESS} strokeWidth={4.5} width={12} />
      </ShowMoreContainer>
    );
  };

  const renderGridTogglerWithWrapper = (gridToggler: JSX.Element) => {
    if (!betsWithBestOdds.length) return null;

    return gridTipVisible ? (
      <SeeMoreOddsTip givenPlacement="right" isVisible={gridTipVisible}>
        {gridToggler}
      </SeeMoreOddsTip>
    ) : (
      gridToggler
    );
  };

  const renderTabletOrDesktop = () => {
    return (
      <ExpanderContent isUnsupported={!allowOddsClickout}>
        {renderSupportMessage()}
        {renderMarketExpander()}
        {renderShowMoreContainer()}
        {renderGridTogglerWithWrapper(
          <GridToggler
            aria-expanded={gridIsExpanded}
            aria-label={compareAllOddsLabel}
            data-testid="compare-odds"
            onClick={handleCompareAllOddsClick}
          >
            <span>{compareAllOddsLabel}</span>
            <Chevron expanded={gridIsExpanded} />
            <Pointer />
          </GridToggler>,
        )}
      </ExpanderContent>
    );
  };

  const translatedRegionalisedSubeventName = () => {
    if (!subeventName) return '';

    const { first, second, separator, success } = extractTeamsFromSubeventName(subeventName);

    if (success) return `${t(first)}${t(separator)}${t(second)}`;
    return t(subeventName);
  };

  const getRegionalisedSubeventStart = () => {
    if (!subeventStartTime) {
      return '';
    }

    return formatInTimeZone(
      subeventStartTime,
      REPUBS[repub].dateProperties.timezone,
      REPUBS[repub].dateProperties.format.timeDayDateShort,
      {
        locale: REPUBS[repub].dateProperties.locale,
      },
    );
  };

  return (
    <AccordionWrapper className={cx(!allowOddsClickout && 'not-supported')} ref={wrapperRef}>
      <Accordion
        header={
          <AccordionHeader data-testid="market-header" id={`market_header_${marketId}`} onClick={toggleMarket}>
            <MarketHeaderText>
              {marketNameLabel}
              {showSubeventData && (
                <span data-testid="market-subevent-data">
                  {translatedRegionalisedSubeventName()} | {getRegionalisedSubeventStart()}
                </span>
              )}
            </MarketHeaderText>
            <Chevron expanded={startsExpanded} strokeWidth={4} width={18} />
          </AccordionHeader>
        }
        id={marketId}
        visible={startsExpanded}
      >
        <DesktopOnly>{renderTabletOrDesktop()}</DesktopOnly>
      </Accordion>
    </AccordionWrapper>
  );
};
